/**
 * Leaderboards — the current match, and the tournament standings.
 *
 * Both render the format's own `StatusChip` and sort on `rankValue`, so this
 * screen has no knowledge of any format. Adding a seventh format needs no
 * change here.
 */

import { useEffect, useMemo, useState } from 'react'
import { clockTime } from '../engine/clock'
import { formatName, matchNameIn, matchNumber } from '../engine/naming'
import { matchCounts } from '../engine/types'
import type { Match, Tournament, UnitTab } from '../engine/types'
import { countersForPlayers } from '../engine/scoring'
import { weekdayOf } from '../select'
import { buildContext, computeAll, matchesInPlayOrder, outstandingCards, pendingCoinFlips, playerName, todayLocal, type PendingFlip } from '../select'
import { type Alert, type Miss, elevate, mergedConfirmations, mergedScores, saveDoc, useStore } from '../store'
import { Bar, Chip, Empty, Row, SectionRule } from '../ui'
import { WeekMatches } from './WeekMatches'
import { Simulate } from './Simulate'
import { Share } from './Share'
import { SeedingBlocked, UnsignedCards, seedingBlockers } from './Unsigned'
import { BirdieNudge } from './BirdieNudge'
import { enablePush, pushState, type PushState } from '../push'

/**
 * Who this result was against.
 *
 * "Won 3 & 2" is half a result on its own — beating Tyler and beating Eric are
 * different afternoons, and the sheet always names both sides. Head-to-head
 * formats have a literal opponent in the pairing; quota has none, so the honest
 * answer there is the field you were ranked in, which is what decides whether
 * your 21 points was worth $300 or nothing.
 */
function opponentOf(doc: Tournament, match: Match, tab: UnitTab): string | undefined {
  if (match.config.kind === 'quota') {
    if (match.config.rankingScope === 'field') return 'v the whole field'
    const group = doc.groups.find((g) => g.id === tab.groupId)
    const rest = (group?.playerIds ?? []).filter((p) => !tab.playerIds.includes(p))
    return rest.length ? `v ${rest.map((p) => playerName(doc, p)).join(' · ')}` : undefined
  }

  const pairing = doc.pairings.find(
    (p) => p.matchId === match.id && p.sides.some((s) => s.id === tab.unitId),
  )
  const other = pairing?.sides.find((s) => s.id !== tab.unitId)
  if (!other) return undefined
  return `v ${other.playerIds.map((p) => playerName(doc, p)).join(' & ')}`
}

/**
 * Opening a card, optionally on one square of it.
 *
 * The second argument is what makes a birdie in the feed land on the birdie
 * rather than on the top-left corner of a twelve-row table. Every other caller
 * omits it and means "just the card".
 */
export type OpenCard = (matchId: string, cell?: { playerId: string; holeNo: number }) => void

const ordinal = (n: number): string => {
  const rem100 = n % 100
  if (rem100 >= 11 && rem100 <= 13) return `${n}th`
  return `${n}${['th', 'st', 'nd', 'rd'][n % 10] ?? 'th'}`
}

/**
 * Every birdie of the week, newest first.
 *
 * This exists because a notification is not a record. It arrives once, it is
 * swiped away, and on iOS most of the twelve will never have granted permission
 * in the first place — that needs the app on the home screen and a deliberate
 * tap. A feed that only showed what had been pushed would therefore be empty
 * for most of the group and wrong for the rest, so the rows are written when
 * the score settles and the push is merely one way of hearing about them.
 *
 * The rows are also allowed to be taken back, which the notifications are not.
 * A hole re-scored to a par withdraws its own row on the next poll, so this is
 * never left insisting on a 3 that the card no longer shows.
 */
function Feed({
  doc,
  alerts,
  onOpenCard,
  /*
   * Passed in rather than rendered inside the birdie list, because the birdie
   * list has an empty state and an unsigned card must not be hidden by "No
   * birdies yet." The first morning of the week is precisely when both are true.
   */
  nudge,
  scores,
  misses,
}: {
  doc: Tournament
  alerts: Alert[]
  /** Putts missed from inside three feet — counted, never scored. */
  misses: Miss[]
  onOpenCard?: OpenCard
  nudge?: React.ReactNode
  /** The week's scores, so the count can be taken net as well as gross. */
  scores: Parameters<typeof buildContext>[3]
}) {
  const [basis, setBasis] = useState<'gross' | 'net'>('gross')
  /*
   * The running count, by man — Scott's ask, taken off the CARDS rather than
   * off this feed. The feed only knows what it announced, which is gross:
   * a net birdie made with a pop was never a notification and would be
   * missing from a net column built on alerts. Counting the week's holes
   * gives both honestly, and gives eagles their own number — an eagle is
   * not a birdie, and the man who made one will say so.
   *
   * Gross by default. Net is the toggle, because net is the argument.
   */
  const { counts, byDay } = useMemo(() => {
    type Pair = {
      gross: { eagles: number; birdies: number }
      net: { eagles: number; birdies: number }
    }
    const both = new Map<string, Pair>()
    const byDay = new Map<string, Pair>()
    for (const m of doc.matches) {
      if (!matchCounts(m)) continue
      const round = doc.rounds.find((r) => r.id === m.roundId)
      if (!round) continue
      const ctx = buildContext(doc, round, m, scores)
      if (!ctx) continue
      const ids = ctx.groups.flatMap((g) => g.playerIds)
      const g = countersForPlayers(ctx, ids, 'gross')
      const n = countersForPlayers(ctx, ids, 'net')
      for (const pid of ids) {
        const row = both.get(pid) ?? {
          gross: { eagles: 0, birdies: 0 },
          net: { eagles: 0, birdies: 0 },
        }
        row.gross.eagles += g[pid]?.eagles ?? 0
        row.gross.birdies += g[pid]?.birdies ?? 0
        row.net.eagles += n[pid]?.eagles ?? 0
        row.net.birdies += n[pid]?.birdies ?? 0
        both.set(pid, row)
      }
      /*
       * The same count again, per DAY — the shape of the week rather than
       * its total. A day is the unit men remember a trip in ("Tuesday was
       * the day everybody made birdies"), and the totals alone cannot say
       * which day that was.
       */
      const key = round.date
      const day = byDay.get(key) ?? {
        gross: { eagles: 0, birdies: 0 },
        net: { eagles: 0, birdies: 0 },
      }
      for (const pid of ids) {
        day.gross.eagles += g[pid]?.eagles ?? 0
        day.gross.birdies += g[pid]?.birdies ?? 0
        day.net.eagles += n[pid]?.eagles ?? 0
        day.net.birdies += n[pid]?.birdies ?? 0
      }
      byDay.set(key, day)
    }
    return { counts: both, byDay }
  }, [doc, scores])

  const tally = [...counts.entries()]
    .map(([playerId, row]) => {
      const side = basis === 'net' ? row.net : row.gross
      return { playerId, ...side, total: side.eagles + side.birdies }
    })
    .filter((t) => t.total > 0)
    .sort((x, y) => y.total - x.total || y.eagles - x.eagles)

  const Tally = () => (
    <div className="px-4 py-2.5 border-b border-rule">
      <div className="flex items-baseline justify-between gap-3 mb-1.5">
        <span className="bc-label text-mute">
          The count{basis === 'net' ? ' — net' : ''}
        </span>
        {/* Gross is the default because gross is what the feed announced;
            net is a tap away because net is what the money argues about. */}
        <div className="flex border border-rule shrink-0">
          {(['gross', 'net'] as const).map((b) => (
            <button
              key={b}
              type="button"
              onClick={() => setBasis(b)}
              className={`bc-label bc-tap px-2.5 py-1 text-[11px] ${
                basis === b ? 'bg-ink text-paper' : 'bg-paper text-mute'
              }`}
            >
              {b === 'gross' ? 'Gross' : 'Net'}
            </button>
          ))}
        </div>
      </div>
      {/* Down the left, then across — the same reading order as every other
          ranked list on the app. */}
      {tally.length === 0 ? (
        <p className="text-[13px] text-mute">Nothing {basis} yet.</p>
      ) : (
        <ul
          className="grid grid-flow-col gap-x-6 gap-y-1"
          style={{ gridTemplateRows: `repeat(${Math.ceil(tally.length / 2)}, auto)` }}
        >
          {tally.map((t) => (
            <li key={t.playerId} className="flex items-baseline justify-between gap-2 text-sm">
              <span className="bc-name truncate min-w-0">{playerName(doc, t.playerId)}</span>
              <span className="shrink-0">
                <span className="bc-num">{t.total}</span>
                {t.eagles > 0 && (
                  <span className="text-[12px] text-signal">
                    {' '}
                    · {t.eagles} eagle{t.eagles === 1 ? '' : 's'}
                  </span>
                )}
              </span>
            </li>
          ))}
        </ul>
      )}
      {/*
        The week by day, under the names that made them — Scott's placement.
        A day is how a trip is remembered, and the per-man column cannot say
        which day the birdies actually fell on; centred under the table it
        reads as the table's total rather than a second list.
      */}
      {(() => {
        const days = [...byDay.entries()]
          .map(([date, row]) => {
            const side = basis === 'net' ? row.net : row.gross
            return { date, n: side.eagles + side.birdies, eagles: side.eagles }
          })
          .filter((d) => d.n > 0)
          .sort((a, b) => (a.date < b.date ? -1 : 1))
        if (days.length === 0) return null
        const total = days.reduce((n, d) => n + d.n, 0)
        return (
          <p className="text-[13px] leading-snug mt-2 pt-2 border-t border-rule text-center">
            {days.map((d, i) => (
              <span key={d.date}>
                {i > 0 && <span className="text-mute"> · </span>}
                <span className="text-mute">{weekdayOf(d.date)} </span>
                <span className="bc-num">{d.n}</span>
                {d.eagles > 0 && <span className="text-signal text-[11px]">+{d.eagles}e</span>}
              </span>
            ))}
            <span className="text-mute"> · </span>
            <span className="bc-label text-mute">All week </span>
            <span className="bc-num">{total}</span>
          </p>
        )
      })()}
      {/*
        The shame ledger, kept where the glory is — Scott's ask. Counted
        from the reports, so it moves the moment somebody taps and the
        moment somebody takes it back. Nothing here touches a card.
      */}
      {(() => {
        const by = new Map<string, number>()
        for (const m of misses) by.set(m.playerId, (by.get(m.playerId) ?? 0) + 1)
        if (by.size === 0) return null
        const rows = [...by.entries()].sort((a, b) => b[1] - a[1])
        const total = rows.reduce((n, [, c]) => n + c, 0)
        return (
          <div className="mb-2 pb-2 border-b border-rule">
            <span className="bc-label text-mute block mb-1">
              Missed inside 3 ft · {total} all week
            </span>
            <p className="text-[13px] leading-snug">
              {rows.map(([pid, n], i) => (
                <span key={pid}>
                  {i > 0 && <span className="text-mute"> · </span>}
                  <span className="bc-name">{playerName(doc, pid)}</span>{' '}
                  <span className="bc-num text-signal">{n}</span>
                </span>
              ))}
            </p>
          </div>
        )
      })()}
    </div>
  )

  if (alerts.length === 0)
    return (
      <>
        {nudge}
        <Tally />
        <div className="px-4 py-10 text-center">
          <p className="text-mute text-sm">No birdies yet.</p>
          {/*
            The delay is deliberate and has to be explained here, or the first
            person to make one comes away thinking the feature is broken. Thirty
            seconds is the window in which a mis-tapped 2 can be corrected before
            twelve phones are told about it.
          */}
          <p className="text-mute text-[13px] leading-snug mt-2 max-w-[34ch] mx-auto">
            Birdies and better show up here about half a minute after they are
            entered — long enough for a mis-tap to be taken back first.
          </p>
        </div>
      </>
    )

  return (
    <>
      {nudge}
      <Tally />
      <ul className="divide-y divide-rule border-b border-rule">
        {[...alerts].reverse().map((a) => {
          const round = doc.rounds.find((r) => r.id === a.roundId)
          const course = doc.courses.find((c) => c.id === round?.courseId)
          const go =
            a.matchId && onOpenCard
              ? () => onOpenCard(a.matchId!, { playerId: a.playerId, holeNo: a.holeNo })
              : undefined
          const Tag = go ? 'button' : 'div'
          return (
            <li key={`${a.roundId}|${a.playerId}|${a.holeNo}`}>
              <Tag
                {...(go ? { onClick: go, type: 'button' as const } : {})}
                className="w-full text-left px-4 py-3 grid grid-cols-[minmax(0,1fr)_auto] gap-3 items-start bc-tap"
              >
                <span className="min-w-0">
                  {/*
                    The achievement leads, not the name. Scanning this list the
                    question is "was there an eagle", and the answer should not
                    be at the end of a sentence.
                  */}
                  <span
                    className={`bc-label block ${a.kind === 'Birdie' ? 'text-mute' : 'text-signal'}`}
                  >
                    {a.kind}
                  </span>
                  <span className="bc-name text-sm block truncate mt-0.5">
                    {playerName(doc, a.playerId)} — {a.gross} on the {ordinal(a.holeNo)}
                  </span>
                  <span className="text-[12px] text-mute block truncate">
                    {/*
                      The round label is usually "Bandon Dunes — Front", which
                      already names the course; printing both gave every row a
                      stutter. Named only when the label does not already say
                      it, since a label like "Championship" still needs telling
                      which course it was played on.
                    */}
                    {[
                      round?.label,
                      course && !round?.label.includes(course.name) ? course.name : null,
                      `par ${a.par}`,
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </span>
                </span>
                <span className="text-right shrink-0">
                  <span className="text-[12px] text-mute block tabular-nums">
                    {new Date(a.announcedAt).toLocaleTimeString([], {
                      hour: 'numeric',
                      minute: '2-digit',
                    })}
                  </span>
                  {go && <span className="text-[12px] text-mute block mt-1">Card &rsaquo;</span>}
                </span>
              </Tag>
            </li>
          )
        })}
      </ul>
      <p className="px-4 py-3 text-[12px] text-mute leading-snug">
        Tap one to open the card it happened on. A hole corrected later drops off
        this list by itself.
      </p>
    </>
  )
}

/**
 * Ask for birdie alerts, on the screen men actually read between shots.
 *
 * The Phone tab has had the real control all along and nobody found it: three
 * of twelve had alerts on for the first round anybody scored. This is the ask,
 * not a second control — it hands off to the same `enablePush` and then gets
 * out of the way for good.
 *
 * Read once on mount rather than polled. The answer only changes when somebody
 * acts on it, and the Board is not a screen that should be doing work.
 */
function BirdieAsk() {
  const [state, setState] = useState<PushState | null>(null)
  useEffect(() => {
    let live = true
    pushState().then((s) => live && setState(s)).catch(() => {})
    return () => {
      live = false
    }
  }, [])
  return (
    <BirdieNudge
      state={state}
      onEnable={async () => {
        const next = await enablePush()
        setState(next)
        return next
      }}
    />
  )
}

export function Board({
  doc,
  onOpenCard,
  onSign,
}: {
  doc: Tournament
  onOpenCard?: OpenCard
  /** Jump to the Score tab on a round, so the man who can sign can sign. */
  onSign?: (roundId: string) => void
}) {
  const scores = useStore(mergedScores)
  const misses = useStore((st) => st.misses)
  const confirmations = useStore(mergedConfirmations)
  const alerts = useStore((s) => s.alerts)
  const me = useStore((s) => s.session?.playerId ?? '')
  const view = computeAll(doc, scores, confirmations)
  const [tab, setTab] = useState<'match' | 'tournament' | 'feed' | 'share'>('match')
  const [simming, setSimming] = useState(false)

  /*
   * Only cards that have outlived their own day.
   *
   * `daysLate > 0` is the whole filter. An unsigned card during the round is a
   * round in progress, not a problem, and a banner that is up all afternoon is
   * one nobody reads by Tuesday — so this stays empty until the morning after,
   * which is when somebody at breakfast can actually go and fix it.
   */
  /*
   * Two different lists, deliberately not one.
   *
   * `blockers` is what is holding the championship DRAW up, and excludes the
   * championship's own round — that card cannot exist before the draw does.
   * `carriedOver` is what the feed nags about, and must NOT exclude it: an
   * unsigned championship card holds up the biggest purse of the week.
   */
  const blockers = seedingBlockers(doc, scores, confirmations)
  const carriedOver = outstandingCards(doc, scores, confirmations, todayLocal()).filter(
    (c) => c.daysLate > 0,
  )

  /**
   * Open on the match being played, not on match 1 all week.
   *
   * A round with holes posted and holes left is the one someone is standing in;
   * failing that, fall back to the most recent round that has started, and only
   * then to the top of the schedule.
   */
  const autoMatchId = (() => {
    // Play order: the most recent round that has STARTED, not the newest row.
    const entries = matchesInPlayOrder(doc).map((m) => view.byMatch[m.id]).filter(Boolean)
    const inPlay = entries.find((e) => e!.tabs.some((t) => t.thru > 0 && !t.complete))
    const started = [...entries].reverse().find((e) => e!.tabs.some((t) => t.thru > 0))
    return (inPlay ?? started ?? entries[0])?.match.id ?? ''
  })()

  // null means "follow the live round"; picking one pins it.
  const [pinnedId, setPinnedId] = useState<string | null>(null)
  const live = view.byMatch[pinnedId ?? autoMatchId] ?? Object.values(view.byMatch)[0]

  // Only the flighted championship splits; every other match is one contest.
  const liveFlights =
    live && live.match.config.kind === 'strokeNet' && live.match.config.flighted &&
    view.flights.length > 0
      ? view.flights
      : null

  /*
   * "Live" is a claim about the golf, not about the tab.
   *
   * The header carried the word twice — as its own title and again as the red
   * dot beside it — and both were unconditional, so a week eight days away with
   * no scores on it announced itself as live. It is live when somebody has
   * actually posted a stroke on the round being shown.
   */
  const playing = (live?.tabs ?? []).some((t) => t.thru > 0)

  return (
    <>
      <Bar
        kicker={tab === 'match' ? live?.round.label : 'All rounds'}
        title={tab === 'match' ? (playing ? 'Live' : 'The match') : tab === 'feed' ? 'Birdies' : 'Standings'}
        sub={
          tab === 'match'
            ? (live ? matchNameIn(doc, live.match) : undefined)
            : tab === 'feed'
              ? 'Every one of them, all week'
              : 'Points and cash to date'
        }
        live={tab === 'match' && playing}
      />
      <BirdieAsk />

      {/*
        Four across rather than three. "Tournament" is the widest label and the
        one with least to lose, so it becomes "Standings" here — which is what
        the screen has always called itself in the masthead anyway.
      */}
      <div className="grid grid-cols-4 border-b border-rule">
        {(['match', 'tournament', 'feed', 'share'] as const).map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setTab(t)}
            className={`bc-label bc-tab py-3 bc-tap text-center ${
              tab === t ? 'bg-ink text-paper' : 'bg-paper text-mute'
            }`}
          >
            {t === 'match'
              ? 'Match'
              : t === 'tournament'
                ? 'Standings'
                : t === 'feed'
                  ? 'Birdies'
                  : 'Post it'}
          </button>
        ))}
      </div>

      {tab === 'match' ? (
        <>
          {live && live.tabs.length > 0 ? (
            liveFlights ? (
              /*
               * The championship is three separate tournaments.
               *
               * Each flight plays for its own purse, so one table of twelve
               * ranked together is not merely harder to read — it is the wrong
               * competition. Second overall means nothing; second in Flight 2
               * is a payout. Splitting them also puts each foursome under its
               * own heading, which is how they are actually walking the course.
               */
              <div>
                {/*
                  What the flights are standing on, before the flights.
                  The draw is seeded off signed cards only, so an unsigned one
                  makes this an incomplete picture — and reading it as the final
                  draw is a thousand-dollar mistake. Renders nothing once the
                  week's cards are in.
                */}
                {!view.seedingComplete && (
                  <SeedingBlocked doc={doc} cards={blockers} onSign={onSign} />
                )}
                {liveFlights.map(({ flight, playerIds }) => {
                  const tabs = live.tabs
                    .filter((t) => t.playerIds.some((p) => playerIds.includes(p)))
                    .sort((a, b) => b.rankValue - a.rankValue)
                  if (tabs.length === 0) return null
                  return (
                    <div key={flight.name}>
                      <SectionRule>{flight.name}</SectionRule>
                      {tabs.map((t, i) => (
                        <Row
                          key={t.unitId}
                          pos={i + 1}
                          name={t.playerIds.map((p) => playerName(doc, p)).join(' & ')}
                          right={<Chip chip={t.chip} />}
                          lead={i === 0}
                          onClick={onOpenCard ? () => onOpenCard(live.match.id) : undefined}
                        />
                      ))}
                    </div>
                  )
                })}
                {/* Anyone the seeding has not placed still has to appear, or a
                    player silently vanishes from the live board. */}
                {(() => {
                  const placed = new Set(liveFlights.flatMap((f) => f.playerIds))
                  const rest = live.tabs
                    .filter((t) => !t.playerIds.some((p) => placed.has(p)))
                    .sort((a, b) => b.rankValue - a.rankValue)
                  if (rest.length === 0) return null
                  return (
                    <div>
                      <SectionRule>Not yet seeded</SectionRule>
                      {rest.map((t, i) => (
                        <Row
                          key={t.unitId}
                          pos={i + 1}
                          name={t.playerIds.map((p) => playerName(doc, p)).join(' & ')}
                          right={<Chip chip={t.chip} />}
                          onClick={onOpenCard ? () => onOpenCard(live.match.id) : undefined}
                        />
                      ))}
                    </div>
                  )
                })()}
              </div>
            ) : live.match.config.kind === 'quota' &&
              live.match.config.rankingScope === 'group' ? (
              /*
                A group-ranked quota is three separate contests, not one of
                twelve — Scott's ask from the course. Ranking the whole field
                in one column puts a man 7th who is actually LEADING his
                foursome, which is the only race he is in. So it splits by
                foursome, each with its own places and its own leader, under
                the tee time they went out on.
              */
              <div>
                {doc.groups
                  .filter((g) => g.roundId === live.match.roundId)
                  .sort((a, b) => a.index - b.index)
                  .map((g) => {
                    const tabs = live.tabs
                      .filter((t) => t.groupId === g.id)
                      .sort((a, b) => b.rankValue - a.rankValue)
                    if (tabs.length === 0) return null
                    return (
                      <div key={g.id}>
                        <div className="px-4 py-1.5 bg-wash border-y border-rule flex items-baseline justify-between gap-3">
                          <span className="bc-label text-mute">
                            {clockTime(g.teeTime) || 'Foursome'}
                          </span>
                          <span className="text-[12px] text-mute truncate min-w-0">
                            {tabs.map((t) => playerName(doc, t.playerIds[0]!)).join(' · ')}
                          </span>
                        </div>
                        {tabs.map((t, i) => (
                          <Row
                            key={t.unitId}
                            pos={i + 1}
                            name={t.playerIds.map((p) => playerName(doc, p)).join(' & ')}
                            right={<Chip chip={t.chip} />}
                            lead={i === 0}
                            onClick={onOpenCard ? () => onOpenCard(live.match.id) : undefined}
                          />
                        ))}
                      </div>
                    )
                  })}
              </div>
            ) : live.match.config.kind === 'quota' || live.match.config.kind === 'strokeNet' ? (
              <div>
                {[...live.tabs]
                  .sort((a, b) => b.rankValue - a.rankValue)
                  .map((t, i) => (
                    <Row
                      key={t.unitId}
                      pos={i + 1}
                      name={t.playerIds.map((p) => playerName(doc, p)).join(' & ')}
                      sub={opponentOf(doc, live.match, t)}
                      right={<Chip chip={t.chip} />}
                      lead={i === 0}
                      onClick={onOpenCard ? () => onOpenCard(live.match.id) : undefined}
                    />
                  ))}
              </div>
            ) : (
              <ContestBoard
                doc={doc}
                match={live.match}
                tabs={live.tabs}
                onOpenCard={onOpenCard}
              />
            )
          ) : (
            <Empty>No scores in yet.</Empty>
          )}

          {/*
            The whole week, under the round being shown. This replaces the
            match dropdown: tapping a round's header pins it above, and unlike
            a listbox the list shows every matchup before you commit to one.
          */}
          <WeekMatches
            doc={doc}
            shownId={live?.match.id}
            me={me}
            onPin={setPinnedId}
            view={view}
          />
        </>
      ) : tab === 'tournament' ? (
        simming ? (
          <Simulate doc={doc} view={view} onBack={() => setSimming(false)} />
        ) : (
          <>
            {/*
              The simulator's front door, moved here from the week view. It
              answers "where would I finish" — a standings question — so it
              lives with the standings, and it is dressed as a BUTTON rather
              than another list row, because on a phone a full-bleed strip
              reads as a heading and Scott walked past his own feature.
            */}
            <div className="px-4 pt-3 pb-1">
              <button
                type="button"
                onClick={() => setSimming(true)}
                className="bc-tap w-full border-2 border-ink px-3 py-3 flex items-center justify-between gap-3"
              >
                <span className="bc-label whitespace-nowrap">Play it out</span>
                <span className="text-mute text-[13px] whitespace-nowrap">
                  pick results, see the flights <span className="text-ink">›</span>
                </span>
              </button>
            </div>
            <TournamentBoard doc={doc} view={view} onOpenCard={onOpenCard} />
          </>
        )
      ) : tab === 'feed' ? (
        <Feed
          doc={doc}
          alerts={alerts}
          scores={scores}
          misses={misses}
          onOpenCard={onOpenCard}
          nudge={<UnsignedCards doc={doc} cards={carriedOver} me={me} onSign={onSign} />}
        />
      ) : (
        <Share doc={doc} view={view} />
      )}
    </>
  )
}

/**
 * One player's week, match by match.
 *
 * The standings answer "who is winning" and nothing else — a number with no
 * account of where it came from, which is exactly what gets argued about on
 * Thursday night. This is the working: every match the player has played, who
 * it was against, what it was worth, and a way through to the card itself.
 *
 * Only matches actually under way appear. A row of nine zeroes on Monday
 * morning is noise, and worse, it reads as nine losses.
 */
function PlayerBreakdown({
  doc,
  view,
  playerId,
  onOpenCard,
}: {
  doc: Tournament
  view: ReturnType<typeof computeAll>
  playerId: string
  onOpenCard?: OpenCard
}) {
  /*
   * A round played for fun keeps its row and loses its money.
   *
   * `byMatch` deliberately holds the full settlement for a non-counting match —
   * the card is supposed to show who would have won — but the awards inside it
   * are notional, and this is the screen a man reads to work out what he is
   * owed. Printed plainly, an evening quota nine claims "6 pts · $300" three
   * lines under a total that contains neither, and the caveat beneath it says
   * "not counted YET", which promises the opposite of the truth.
   *
   * Dropping the row instead would be worse in the other direction: he played
   * those nine holes and wants to see them.
   */
  const rows = matchesInPlayOrder(doc)
    .map((m) => view.byMatch[m.id])
    .filter((e): e is NonNullable<typeof e> => Boolean(e))
    .map((entry) => ({
      entry,
      counts: matchCounts(entry.match),
      tab: entry.tabs.find((t) => t.playerIds.includes(playerId)),
      award: matchCounts(entry.match)
        ? entry.settlement.awards.find((a) => a.playerId === playerId)
        : undefined,
    }))
    .filter((r) => r.tab && r.tab.thru > 0)

  if (rows.length === 0) {
    return (
      <p className="px-4 py-3 text-[13px] text-mute bg-wash border-b border-rule">
        Nothing played yet.
      </p>
    )
  }

  return (
    <div className="bg-wash border-b border-rule">
      {rows.map(({ entry, tab, award, counts }) => {
        const against = opponentOf(doc, entry.match, tab!)
        return (
          <button
            key={entry.match.id}
            type="button"
            onClick={onOpenCard ? () => onOpenCard(entry.match.id) : undefined}
            className="w-full text-left px-4 py-2.5 border-b border-rule last:border-b-0 grid grid-cols-[minmax(0,1fr)_auto] gap-3 items-start bc-tap"
          >
            <span className="min-w-0">
              <span className="bc-name text-xs block truncate">{matchNameIn(doc, entry.match)}</span>
              {against && (
                <span className="text-[12px] text-mute block truncate">{against}</span>
              )}
              <span className="text-[12px] text-mute block">
                {/* The award's own words when it has settled, the live chip
                    while it has not — "Won 3 & 2" versus "2 UP thru 14". */}
                {award?.label ?? `${tab!.chip.primary} · ${tab!.chip.secondary}`}
                {!tab!.complete && ' · still out there'}
              </span>
              {/*
                Per PLAYER, not per match — a signed foursome's result is in
                the ledger while another group is still on the course, and
                this line told that man his settled card was "not signed for".
                The two remaining waits are different facts and say so: his
                own card missing a signature, or a game against the field
                waiting on everyone else's.
              */}
              {counts && !view.counted(entry.match.id, playerId) && (
                <span className="text-[12px] text-signal block">
                  {view.signedCard(entry.match.roundId, tab!.groupId)
                    ? 'Signed — settles when the whole field is in'
                    : 'Not signed for — not counted yet'}
                </span>
              )}
            </span>
            <span className="text-right shrink-0">
              <span
                className={`bc-num text-sm block leading-none ${
                  view.counted(entry.match.id, playerId) ? '' : 'text-mute'
                }`}
              >
                {award ? `${award.points} pt${award.points === 1 ? '' : 's'}` : '—'}
              </span>
              <span className="text-[12px] text-mute block mt-0.5">
                {award && award.cash > 0 ? `$${award.cash}` : ''}
              </span>
            </span>
          </button>
        )
      })}
      <p className="px-4 py-2 text-[12px] text-mute leading-snug">
        Tap a match to open its card.
      </p>
    </div>
  )
}

/**
 * Say where in the week the standings are from.
 *
 * The header read "Seeding for the final", which is what this table is FOR
 * right up until the final is played and then is exactly the wrong frame — it
 * invites you to ask why the man at the top has not made the most money, when
 * by then the points are a spent currency and the cash is the result.
 *
 * Naming the last signed match answers the question the header was dodging:
 * these standings are as of here, and there is that much still to play.
 */
export function standingsHeading(
  doc: Pick<Tournament, 'matches' | 'rounds'>,
  finalMatchIds: ReadonlySet<string>,
): string {
  /*
   * Only the matches that count, on both sides of the sum.
   *
   * A round played for fun is never signed — there is nothing to settle — so
   * leaving it in the denominator means "Final standings" can never be reached
   * once anybody adds an evening nine, and leaving it in the numerator lets it
   * become the headline the week is measured from.
   */
  // In play order: `settled[settled.length - 1]` is otherwise the last match
  // ADDED to the document, which after a split or an ad-hoc round is not the
  // last one played.
  const counting = matchesInPlayOrder(doc).filter(matchCounts)
  const settled = counting.filter((m) => finalMatchIds.has(m.id))
  const last = settled[settled.length - 1]
  if (!last) return 'Standings'
  if (settled.length === counting.length) return 'Final standings'
  // Labels run to "Match 6c · Quota (field)"; the part before the dot is the
  // half anybody says out loud.
  /*
   * The number, not the stored label. Labels stopped being written when names
   * became derived, so this printed whatever string the match was created with
   * — "Standings after Bandon Dunes — Front" for a round added on a tee.
   */
  const n = matchNumber(last)
  return `Standings after ${n === '' ? formatName(last.config) || 'the last card' : `Match ${n}`}`
}

/**
 * The live head-to-head board, foursome by foursome, two columns.
 *
 * Twelve sorted rows was a ladder of names for six contests — Scott's call:
 * the real estate says two columns, each foursome a block of its own
 * contests, so the whole round reads in half the height. Grouping by contest
 * also beats the global sort it replaces for these formats: "2 UP" only
 * means anything beside the man it is up ON, and sorting by rank put every
 * pair of opponents in different rows.
 *
 * Quota and stroke play never come here — everyone really is ranked against
 * the field there, and the sorted ladder stays the honest shape for them.
 */
function ContestBoard({
  doc,
  match,
  tabs,
  onOpenCard,
}: {
  doc: Tournament
  match: Match
  tabs: UnitTab[]
  onOpenCard?: OpenCard
}) {
  const groups = doc.groups
    .filter((g) => g.roundId === match.roundId)
    .sort((a, b) => a.index - b.index)
  const tabOf = (unitId: string) => tabs.find((t) => t.unitId === unitId)

  /*
   * Two columns again — Scott's call after seeing one — but each contest is
   * now a small CARD in the week view's grammar: a name to a line, the thin
   * v-rule between them, the standing on the winning side's own row. A name
   * on its own line has the full half-width, so "Wittwer v Scott P." never
   * loses letters, and six matches sit in three tight rows.
   */
  const contests = groups.flatMap((g) =>
    doc.pairings
      .filter((p) => p.matchId === match.id && p.groupId === g.id)
      .sort((a, b) => a.index - b.index),
  )
  return (
    <div
      className="grid grid-flow-col gap-x-4 gap-y-3 px-4 py-2.5"
      style={{ gridTemplateRows: `repeat(${Math.ceil(contests.length / 2)}, auto)` }}
    >
      {contests.map((p) => {
        const [a, b] = p.sides
        const ta = tabOf(a!.id)
        const tb = tabOf(b!.id)
        const names = (side: typeof a) =>
          side!.playerIds.map((x) => playerName(doc, x)).join(' & ')
        const leadA = ta && tb ? ta.rankValue > tb.rankValue : Boolean(ta && !tb)
        const leadB = ta && tb ? tb.rankValue > ta.rankValue : Boolean(tb && !ta)
        const square = ta && tb && ta.rankValue === tb.rankValue
        const chip = (t: UnitTab | undefined, on: boolean) =>
          t && on ? (
            <span className="text-right shrink-0">
              <span className="bc-num text-xs leading-none">{t.chip.primary}</span>
              <span className="text-[10px] text-mute ml-1">{t.chip.secondary}</span>
            </span>
          ) : null
        return (
          <button
            key={p.id}
            type="button"
            onClick={onOpenCard ? () => onOpenCard(match.id) : undefined}
            className="bc-tap min-w-0 text-left text-xs leading-snug"
          >
            <span className="flex items-baseline justify-between gap-2">
              <span className={`bc-name ${leadB ? 'text-mute' : ''}`}>{names(a)}</span>
              {chip(ta, leadA || Boolean(square))}
            </span>
            <span aria-hidden="true" className="flex items-center gap-1.5 py-0.5">
              <span className="h-px flex-1 bg-rule" />
              <span className="text-[10px] font-semibold uppercase text-mute leading-none">
                v
              </span>
              <span className="h-px flex-1 bg-rule" />
            </span>
            <span className="flex items-baseline justify-between gap-2">
              <span className={`bc-name ${leadA ? 'text-mute' : ''}`}>{names(b)}</span>
              {chip(tb, leadB)}
            </span>
          </button>
        )
      })}
    </div>
  )
}

/**
 * A tie the rules could not break, and the coin that settles it — for real.
 *
 * The engine's coin flip is seeded and deterministic, which keeps the board
 * stable between refreshes, but Scott's ruling is that real stakes get a real
 * toss in front of the group. So while a flip is owed, the seeded order only
 * STANDS IN, this banner says so loudly, and an admin performs the ceremony:
 * pick who calls heads, flip, watch it land, and record it. The recorded
 * resolution goes into `doc.tiebreaks`, where `breakTie` reads it before ever
 * consulting the seed — so the result is permanent, on every phone.
 *
 * One physical coin settles every scope the same tie appears in (standings
 * and seeding are two lists, not two ties), which is why this takes the whole
 * group of pending flips for one set of contenders.
 *
 * A tie of three or more gets a draw of lots instead — a coin has two sides,
 * and this will likely never run even once for two.
 */
function CoinCeremony({ doc, flips }: { doc: Tournament; flips: PendingFlip[] }) {
  const isAdmin = useStore((st) => Boolean(st.session?.isAdmin))
  const docVersion = useStore((st) => st.docVersion)
  const [headsId, setHeadsId] = useState<string | null>(null)
  const [phase, setPhase] = useState<'idle' | 'spinning' | 'landed' | 'saving' | 'saved'>('idle')
  const [landed, setLanded] = useState<{ face: 'heads' | 'tails'; order: string[] } | null>(null)
  const [error, setError] = useState<string | null>(null)

  const contenders = flips[0]!.contenders
  const two = contenders.length === 2
  const name = (id: string) => playerName(doc, id)
  const tails = two ? contenders.find((c) => c !== headsId) : undefined

  const stakes = flips
    .map((f) =>
      f.scope === 'standings'
        ? 'the standings'
        : f.scope === 'seeding'
          ? 'the championship seeding'
          : (() => {
              const m = doc.matches.find(
                (x) => f.scope === `match:${x.id}` || f.scope.startsWith(`match:${x.id}:`),
              )
              return m ? `the ladder in ${matchNameIn(doc, m)}` : 'a match ladder'
            })(),
    )
    .filter((v, i, a) => a.indexOf(v) === i)
    .join(' and ')

  const land = (face: 'heads' | 'tails', order: string[]) => {
    setLanded({ face, order })
    setPhase('spinning')
    window.setTimeout(() => setPhase('landed'), 1500)
  }
  const flip = () => {
    if (!headsId || !tails) return
    const face = Math.random() < 0.5 ? 'heads' : 'tails'
    const winner = face === 'heads' ? headsId : tails
    land(face, [winner, contenders.find((c) => c !== winner)!])
  }
  const draw = () =>
    land(
      'heads',
      [...contenders]
        .map((id) => ({ id, r: Math.random() }))
        .sort((a, b) => a.r - b.r)
        .map((x) => x.id),
    )

  const record = async () => {
    if (!landed) return
    setPhase('saving')
    setError(null)
    try {
      // Roster admins elevate without a PIN; anyone else fails here, plainly.
      await elevate()
      const detail = two
        ? `coin flip — ${name(headsId!)} heads, ${name(tails!)} tails: landed ${landed.face}`
        : `drawn by lot: ${landed.order.map(name).join(', ')}`
      const rows = flips.map((f) => ({
        scope: f.scope,
        contenders: [...f.contenders],
        resolvedBy: 'coinFlip' as const,
        detail,
        order: [...landed.order],
      }))
      await saveDoc({ ...doc, tiebreaks: [...doc.tiebreaks, ...rows] }, docVersion)
      setPhase('saved')
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save the result')
      setPhase('landed')
    }
  }

  return (
    <div data-coin-flip className="border-b-2 border-signal bg-wash px-4 py-3">
      <span className="bc-label text-signal block">Coin flip required</span>
      <p className="text-[13px] leading-snug mt-1">
        <b className="bc-name">{contenders.map(name).join(' and ')}</b> are dead level and
        the rules are out of tiebreakers. The coin decides {stakes}.
      </p>

      {phase === 'idle' && !isAdmin && (
        <p className="text-[12px] text-mute mt-1">
          An admin performs the flip. Until then the order shown is a stand-in.
        </p>
      )}

      {phase === 'idle' && isAdmin && two && (
        <>
          <p className="text-[12px] text-mute mt-2">Tap the man who is heads:</p>
          <div className="flex flex-wrap gap-2 mt-1.5">
            {contenders.map((id) => (
              <button
                key={id}
                type="button"
                aria-pressed={headsId === id}
                onClick={() => setHeadsId(id)}
                className={`bc-tap px-3 border ${
                  headsId === id ? 'bg-ink text-paper border-ink' : 'border-rule bg-paper'
                }`}
              >
                {name(id)}
              </button>
            ))}
            <button
              type="button"
              disabled={!headsId}
              onClick={flip}
              className="bc-tap px-3 bg-signal text-paper disabled:opacity-40"
            >
              Flip the coin
            </button>
          </div>
          {headsId && tails && (
            <p className="text-[12px] text-mute mt-1.5">
              {name(headsId)} is heads · {name(tails)} is tails
            </p>
          )}
        </>
      )}

      {phase === 'idle' && isAdmin && !two && (
        <button type="button" onClick={draw} className="bc-tap px-3 mt-2 bg-signal text-paper">
          Draw the order
        </button>
      )}

      {phase !== 'idle' && (
        <div className="flex flex-col items-center py-3">
          <div className={`coin ${phase === 'spinning' ? 'coin-spinning' : ''}`} aria-hidden="true">
            {phase === 'spinning' ? '' : landed?.face === 'heads' ? 'H' : 'T'}
          </div>
          {phase !== 'spinning' && landed && (
            <>
              <p className="coin-winner bc-name text-3xl font-extrabold mt-3">
                {name(landed.order[0]!)}
              </p>
              <p className="text-[12px] text-mute mt-0.5">
                {two
                  ? `Landed ${landed.face} — ${name(landed.order[0]!)} takes it.`
                  : landed.order.map(name).join(' → ')}
              </p>
              {phase === 'saved' ? (
                <p className="text-[12px] text-mute mt-2">
                  Recorded. The standings now stand on it, on every phone, for good.
                </p>
              ) : (
                <div className="flex gap-2 mt-3">
                  <button
                    type="button"
                    disabled={phase === 'saving'}
                    onClick={record}
                    className="bc-tap px-3 bg-ink text-paper disabled:opacity-40"
                  >
                    {phase === 'saving' ? 'Recording…' : 'Record the result'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setPhase('idle')
                      setLanded(null)
                    }}
                    className="bc-tap px-3 border border-rule bg-paper"
                  >
                    Start over
                  </button>
                </div>
              )}
              {error && <p className="text-[12px] text-signal mt-1.5">{error}</p>}
            </>
          )}
        </div>
      )}
    </div>
  )
}

function TournamentBoard({
  doc,
  view,
  onOpenCard,
}: {
  doc: Tournament
  view: ReturnType<typeof computeAll>
  onOpenCard?: OpenCard
}) {
  // One at a time: opening a second closes the first, so the list stays a list
  // rather than becoming a wall.
  const [openId, setOpenId] = useState<string | null>(null)
  const flightOf = (playerId: string): string | undefined =>
    view.flights.find((f) => f.playerIds.includes(playerId))?.flight.name.replace('Flight ', 'F')

  /*
   * Say where in the week these numbers are from.
   *
   * The header read "Seeding for the final", which is what this table is FOR
   * right up until the final is played and then is exactly the wrong frame —
   * it invites you to ask why the man at the top has not made the most money,
   * when by then the points are a spent currency and the cash is the result.
   *
   * Naming the last signed match answers the question the header was dodging:
   * these standings are as of here, and there is that much still to play.
   */
  const heading = standingsHeading(doc, view.finalMatchIds)

  /*
   * Only once the final has been signed for is anybody the champion.
   *
   * The same gate the flight-finish ranking uses, and for the same reason: the
   * man on top on Tuesday leads a points table that exists to seed the
   * championship, and calling him champion would be naming a winner three days
   * before the thing that decides it has been played.
   */
  // The FLIGHTED stroke-play match. A bare card added for fun is stroke play
  // too, and one that somebody signed would otherwise crown a champion.
  const champMatch = doc.matches.find((m) => m.config.kind === 'strokeNet' && m.config.flighted)
  const champDecided = Boolean(champMatch && view.finalMatchIds.has(champMatch.id))

  return (
    <>
      <SectionRule>{heading}</SectionRule>
      {(() => {
        /*
         * The standings are the ledger, not the live scoreboard.
         *
         * A foursome's own contests settle the moment its card is signed, so
         * during a round these totals fill in card by card — what is here is
         * settled, and what is not here is a match still out. Saying so is
         * what stops the lag reading as a bug, and the "not final" is the
         * disclosure Scott asked for: a position can still move until the
         * last card of the last live match is in.
         */
        /*
         * Nobody is waiting on a round played for fun. It settles nothing, so
         * naming it here asks twelve men to chase a signature that would not
         * move a single point.
         */
        const unfinished = doc.matches.filter(
          (m) =>
            matchCounts(m) &&
            !view.finalMatchIds.has(m.id) &&
            view.byMatch[m.id]?.tabs.some((t) => t.thru > 0),
        )
        if (unfinished.length === 0) return null
        return (
          <p className="px-4 pt-1 pb-2 text-[13px] text-mute leading-snug">
            <b className="bc-name text-ink">
              {unfinished.length} {unfinished.length === 1 ? 'match is' : 'matches are'}
            </b>{' '}
            yet to finish, so these standings are not final. Points and money settle as
            each foursome signs its card — a game against the whole field settles when
            the last card of the round is in.
          </p>
        )
      })()}
      {/*
        Any tie the rules could not break, before the rows it would reorder.
        One ceremony per set of contenders — the same pair tied in standings
        and seeding owes one coin, not two.
      */}
      {(() => {
        const flips = pendingCoinFlips(doc, view)
        if (flips.length === 0) return null
        const bySet = new Map<string, PendingFlip[]>()
        for (const f of flips) {
          const k = f.contenders.join(',')
          if (!bySet.has(k)) bySet.set(k, [])
          bySet.get(k)!.push(f)
        }
        return [...bySet.entries()].map(([k, group]) => (
          <CoinCeremony key={k} doc={doc} flips={group} />
        ))
      })()}
      {view.standings.rows.some((r) => r.points > 0) ? (
        <div>
          {view.standings.rows.map((r, i) => (
            <div key={r.playerId}>
              {/*
                Where a projected flight ends, a seam — but ONLY when the
                flights actually run in order down this list. Standings and
                seeding break ties with DIFFERENT chains (the sheet's rule:
                a seeding tie breaks on where you finished in a round), so a
                man can honestly sit 6th on points and still hold an F1 seat.
                On such a week, seams at every flight change carved the list
                into confetti — Scott's screenshot. When the two orders agree
                the seams say something; when they disagree the chips alone
                carry it, and the "Ties" table below shows the why.
              */}
              {i > 0 &&
                view.standings.rows.every((x, j) =>
                  j === 0
                    ? true
                    : (flightOf(view.standings.rows[j - 1]!.playerId) ?? 'F9') <=
                      (flightOf(x.playerId) ?? 'F9'),
                ) &&
                flightOf(r.playerId) !== flightOf(view.standings.rows[i - 1]!.playerId) && (
                  <div aria-hidden="true" className="h-2 bg-wash border-y border-rule" />
                )}
              <Row
                pos={r.position}
                name={playerName(doc, r.playerId)}
                sub={champDecided && i === 0 ? 'Woodsmen Champion' : undefined}
                tag={flightOf(r.playerId)}
                lead={i === 0}
                onClick={() => setOpenId(openId === r.playerId ? null : r.playerId)}
                right={
                  <div className="text-right">
                    <div className="bc-num text-base leading-none">{r.points}</div>
                    <div className="text-[12px] text-mute font-semibold uppercase tracking-wider mt-0.5">
                      {r.cash > 0 ? `$${r.cash}` : 'pts'}
                    </div>
                  </div>
                }
              />
              {openId === r.playerId && (
                <PlayerBreakdown
                  doc={doc}
                  view={view}
                  playerId={r.playerId}
                  onOpenCard={onOpenCard}
                />
              )}
            </div>
          ))}
          {/*
            Every tie and exactly what broke it, numbers included — Scott's
            ask: the chain settles ties as the rules say, and the math prints
            so an admin can CHECK it rather than trust it. The engine writes
            player ids into the detail (it knows no display names), so they
            are swapped for names here, longest id first so "scottp" can never
            be half-replaced by a shorter id it contains.
          */}
          {(() => {
            const pts = new Map(view.standings.rows.map((r) => [r.playerId, r.points]))
            const broken = view.standings.tiebreaks.filter((r) =>
              r.contenders.some((id) => (pts.get(id) ?? 0) > 0),
            )
            if (broken.length === 0) return null
            const withNames = (detail: string): string =>
              [...doc.players]
                .sort((a, b) => b.id.length - a.id.length)
                .reduce(
                  (acc, pl) => acc.split(pl.id).join(playerName(doc, pl.id)),
                  detail,
                )
            return (
              <div className="px-4 py-3 text-[13px] text-mute leading-snug">
                <span className="bc-label block pb-1">Ties, and what broke them</span>
                {broken.map((r) => (
                  <p key={r.contenders.join(',')} className="pb-0.5">
                    {r.contenders.map((id) => playerName(doc, id)).join(' · ')} —{' '}
                    {r.resolvedBy === 'coinFlip' ? 'coin flip' : withNames(r.detail)}.
                  </p>
                ))}
              </div>
            )
          })()}
        </div>
      ) : (
        <Empty>Nothing settled yet.</Empty>
      )}
    </>
  )
}
